/**
 * Статусы позиции журнала на этапе оформления заказа и их «финальность».
 *
 * Бэк ведёт позицию по статусам: 0 — корзина, 1 — заказ отправляется поставщику,
 * 2 — в обработке, 3 и выше — заказ живёт своей жизнью (подтверждение, работа,
 * отгрузка, отказ поставщика). Отрицательные статусы — отказ самого оформления:
 * −3 «закончились токены» (по SER-1295 биллинг отклоняет оформление при нулевом
 * балансе, а текста бэк не присылает), −1 отладочная ошибка.
 *
 * Фронт после `make_order` держит хеши заказанных позиций в «ожидании» и снимал
 * отметку только при `status >= 3`. Отрицательные статусы под это условие не
 * попадали: спиннер «Заказ отправляется поставщику» крутился вечно, поллинг
 * статусов не останавливался, а пользователь так и оставался с тостом
 * «Заказы успешно оформлены» при том, что заказ не создан (CAB-602 п.2).
 *
 * Поэтому «финал» здесь — и `>= 3`, и любой отрицательный статус; отрицательный
 * при этом отдельно распознаётся как отказ, о котором нужно сказать вслух.
 */

interface OrderAttempt {
  error?: unknown;
}

interface JournalOrderItem {
  status?: unknown;
  /** Сырой статус последней попытки оформления: в нём закодирован её номер. */
  last_mo_status?: unknown;
  error?: unknown;
  mo_attemps?: OrderAttempt[] | Record<string, OrderAttempt> | null;
}

/** Первый статус «живого» заказа: с него позиция уже не в процессе оформления. */
const PLACED_STATUS = 3;

/**
 * Бэк кодирует номер попытки оформления в статусе: `(n - 1) * 1000 + status`
 * (1, 1001, 2001 — «в работе»; 3/1003 — успех; 4/1004 — ошибка). Сам код статуса —
 * остаток от деления на этот шаг (CAB-683).
 */
const ATTEMPT_STEP = 1000;

/**
 * Код статуса позиции без номера попытки. Строка журнала после повторного
 * оформления (CAB-683) несёт статус вида 1004, а сравнивать и показывать нужно
 * именно 4: везде, где статус читается или сравнивается, брать это значение.
 * Мусор и пустое значение — null.
 */
export function journalStatusCode(status: unknown): number | null {
  if (status === null || status === undefined || status === "") return null;
  const value = Number(status);
  return Number.isFinite(value) ? value % ATTEMPT_STEP : null;
}

function statusOf(item: JournalOrderItem | null | undefined): number | null {
  return journalStatusCode(item?.status);
}

/**
 * Сырой `last_mo_status` строки: в нём закодирован номер попытки оформления
 * ((n − 1) · 1000 + код). По нему отличаем итог новой попытки от прежнего
 * статуса — повтор упавшей строки возвращается в тот же код 4, и без этого
 * «оформляется» висело бы на ней до тайм-аута (CAB-683).
 */
export function orderAttemptMarker(item: JournalOrderItem | null | undefined): number | null {
  const raw = item?.last_mo_status;
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/** Статус «оформление у поставщика завершилось ошибкой» — единственный, который можно повторить. */
const ORDER_FAILED_STATUS = 4;

/** Статус «заказ отправляется поставщику» — под ним в таблице крутится спиннер. */
const ORDER_IN_PROGRESS_STATUS = 1;

/** Типы строк журнала, по которым бэк разрешает повторное оформление: корзина и заказ. */
const RETRYABLE_TYPES = new Set([1, 2]);

/**
 * Строку журнала можно оформить повторно тем же `thash` (CAB-683): позиция
 * корзины или заказа, чья последняя попытка закончилась ошибкой (код 4).
 * Позиции в работе (1) и успешно заказанные (3) повтору не подлежат — бэк на
 * них отвечает отказом «по данной позиции уже было оформление».
 */
export function isOrderRetryable(item: { type?: unknown; status?: unknown } | null | undefined): boolean {
  if (!item) return false;
  return RETRYABLE_TYPES.has(Number(item.type)) && journalStatusCode(item.status) === ORDER_FAILED_STATUS;
}

/**
 * Из какого статуса позицию отправили оформляться: 4 — повтор строки с ошибкой,
 * иначе 0 — корзина. Фронтовая отметка «оформляется»/«отказано» действует, пока
 * строка стоит в этом статусе; как только бэк сдвинул его сам, его правда важнее.
 * Текущий статус строки для этого не годится: поллинг мог успеть сдвинуть его до
 * ответа make_order, и отметка ждала бы возврата в статус, которого уже не будет.
 */
export function orderPlacementStartStatus(item: { type?: unknown; status?: unknown } | null | undefined): number {
  return isOrderRetryable(item) ? ORDER_FAILED_STATUS : 0;
}

// Подписи статусов отказа (те же, что в тултипе колонки «Статус»): их
// подставляем, когда бэк отказал, но текста причины не дал — типичный случай −3.
const FAILURE_LABEL_KEYS: Record<number, string> = {
  [-1]: "journalTable.statusDebugError",
  [-3]: "journalTable.statusOutOfTokens",
  4: "journalTable.statusOrderError",
  9: "journalTable.statusUnexpectedError",
};

/** Ключ локализации для статуса отказа; null — статус незнакомый. */
export function orderFailureLabelKey(status: number): string | null {
  return FAILURE_LABEL_KEYS[status] ?? null;
}

/**
 * Коды, в которых попытка оформления закончилась без заказа: 4 — поставщик не
 * принял заказ, 9 — непредвиденная ошибка. Раньше «отказом» считались только
 * отрицательные статусы, а 4 и 9 проходили как обычный финал — и клиент, уже
 * увидев тост «Заказы успешно оформлены», о провале не узнавал вовсе (CAB-683).
 */
const FAILED_ORDER_STATUSES = new Set([ORDER_FAILED_STATUS, 9]);

/** Оформление позиции закончилось без заказа: отказ бэка или отказ поставщика. */
export function isOrderPlacementFailed(item: JournalOrderItem | null | undefined): boolean {
  const status = statusOf(item);
  return status !== null && (status < 0 || FAILED_ORDER_STATUSES.has(status));
}

/**
 * Оформление позиции завершено — успехом или отказом. Пока false, позиция
 * остаётся в «ожидании» и поллинг статусов имеет смысл.
 */
export function isOrderPlacementSettled(item: JournalOrderItem | null | undefined): boolean {
  const status = statusOf(item);
  return status !== null && (status >= PLACED_STATUS || status < 0);
}

/**
 * Текст последней попытки оформления (`mo_attemps`) — им бэк объясняет причину
 * отказа точнее общей подписи статуса. Формат приходит и списком, и объектом.
 */
export function lastOrderErrorText(item: JournalOrderItem | null | undefined): string | null {
  const attempts = item?.mo_attemps;
  let list: OrderAttempt[] = [];
  if (Array.isArray(attempts)) list = attempts;
  else if (attempts) list = Object.values(attempts);
  const last = list.length > 0 ? list[list.length - 1] : null;
  const attemptError = typeof last?.error === "string" ? last.error.trim() : "";
  if (attemptError.length > 0) return attemptError;
  const itemError = typeof item?.error === "string" ? item.error.trim() : "";
  return itemError.length > 0 ? itemError : null;
}

/**
 * Человекочитаемые причины отказа по позициям: текст бэка, иначе подпись статуса.
 * Повторы склеиваем — одна и та же причина по десяти позициям читается как одна.
 */
export function describeOrderRejections(
  rejected: Array<{ status: number; error: string }>,
  translate: (key: string) => string,
): string {
  const reasons = rejected.map((item) => {
    if (item.error.length > 0) return item.error;
    const key = orderFailureLabelKey(item.status);
    return key ? translate(key) : "";
  });

  return Array.from(new Set(reasons.filter((reason) => reason.length > 0))).join("; ");
}

/**
 * Что фронт уже знает о строке, отправленной оформляться. `make_order` только
 * принимает заявку: заказ у поставщика создаётся асинхронно, и его итог
 * приходит сменой статуса строки. Поэтому отметку ведём по увиденному
 * состоянию, а не по времени (CAB-683).
 */
export interface PlacementProgress {
  /** Код статуса строки, который фронт уже учёл (без номера попытки). */
  seenStatus: number;
  /** `last_mo_status` на тот же момент — отличает новую попытку от прежней. */
  seenAttempt: number | null;
  /** Когда это состояние увидено: от него отсчитывается тайм-аут ожидания. */
  seenAt: number;
}

/** Итог попытки оформления: заказ создан или его нет. */
export type PlacementOutcome = "placed" | "failed";

/** Что делать с отметкой «оформляется» после очередного обновления строки. */
export type PlacementDecision =
  | { action: "wait" }
  | { action: "advance"; seenStatus: number; seenAttempt: number | null }
  | { action: "resolve"; outcome: PlacementOutcome }
  | { action: "drop" };

/**
 * Решение по отметке оформления на очередном цикле поллинга.
 *
 * - `wait` — строка стоит там же, где мы её оставили: бэк ещё не взялся;
 * - `advance` — бэк сдвинул строку, но попытка не закончилась (отправляется,
 *   в обработке): запоминаем новое состояние и ждём дальше;
 * - `resolve` — попытка закончилась, итог известен и о нём нужно сказать вслух;
 * - `drop` — строки больше нет или ждать дольше бессмысленно; молча забываем.
 *
 * Тайм-аут считается от последнего увиденного изменения, а не от отправки:
 * медленный поставщик не должен обрывать ожидание на полпути.
 */
export function decidePlacementProgress(
  progress: PlacementProgress,
  item: JournalOrderItem | null | undefined,
  now: number,
  timeoutMs: number,
): PlacementDecision {
  if (!item) return { action: "drop" };

  const status = statusOf(item);
  if (status === null) return { action: "drop" };

  const attempt = orderAttemptMarker(item);
  const moved = status !== progress.seenStatus || attempt !== progress.seenAttempt;

  if (!moved) {
    return now - progress.seenAt < timeoutMs ? { action: "wait" } : { action: "drop" };
  }

  if (!isOrderPlacementSettled(item)) {
    return { action: "advance", seenStatus: status, seenAttempt: attempt };
  }

  return { action: "resolve", outcome: isOrderPlacementFailed(item) ? "failed" : "placed" };
}

/** Как позиция называется в сообщении: «артикул бренд». */
export function journalItemLabel(item: { article?: unknown; brand?: unknown } | null | undefined): string {
  return [item?.article, item?.brand]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter((part) => part.length > 0)
    .join(" ");
}

/**
 * Перечисление позиций для сообщения. Длинный список обрезаем: тост на два
 * десятка строк не читают, а в узком окне телефона он занимает пол-экрана.
 */
export function joinOrderDetails(lines: string[], limit: number, describeRest: (rest: number) => string): string {
  const visible = lines.filter((line) => line.length > 0);
  if (visible.length <= limit) return visible.join("; ");
  return [...visible.slice(0, limit), describeRest(visible.length - limit)].join("; ");
}

/**
 * Статус строки, каким его видит пользователь: пока фронт ведёт оформление и бэк
 * не сдвинул строку с того места, откуда её отправили, показываем свою отметку —
 * спиннер «отправляется» или крест отказа; как только бэк сдвинул статус сам,
 * его правда важнее (CAB-602 п.2).
 *
 * По этому же значению судят кнопки панели. Иначе выходило расхождение: в ячейке
 * крутился спиннер, а «Повторить заказ» над той же строкой продолжал гореть,
 * потому что в данных строки всё ещё стоял прежний статус (CAB-683).
 */
export function effectiveOrderStatus(
  item: { status?: unknown } | null | undefined,
  placement: { kind: "pending" | "failed"; status?: number; seenStatus: number } | null | undefined,
): number {
  const status = journalStatusCode(item?.status) ?? 0;
  if (!placement || placement.seenStatus !== status) return status;
  if (placement.kind === "failed") return placement.status ?? status;
  return ORDER_IN_PROGRESS_STATUS;
}
